"use client";

import { usePathname } from "next/navigation";
import { useEffect, useLayoutEffect } from "react";

import { assignDocument, reloadDocument } from "./document-navigation";
import {
  WINGIFY_EXCLUDED_PATH,
  isWingifyLoaded,
} from "./wingify-excluded-paths";

/** Same-origin payment-route URL a plain left click on `event` would follow. */
function excludedLinkTarget(event: MouseEvent): string | undefined {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    !(event.target instanceof Element)
  ) {
    return undefined;
  }

  const anchor = event.target.closest("a[href]");
  if (
    !(anchor instanceof HTMLAnchorElement) ||
    anchor.hasAttribute("download") ||
    (anchor.target && anchor.target !== "_self")
  ) {
    return undefined;
  }

  const url = new URL(anchor.href);
  if (url.origin !== window.location.origin) {
    return undefined;
  }

  return WINGIFY_EXCLUDED_PATH.test(url.pathname) ? url.href : undefined;
}

/**
 * Wingify loaded on a marketing page keeps running after a client-side
 * navigation, so payment routes must get a fresh document where the inline
 * guard keeps Wingify out.
 *
 * Link clicks into a payment route become full document loads before the
 * route renders. Navigations that bypass links (`router.push`) reload the
 * document from a layout effect, before any passive effects on the route run.
 */
export function WingifyExcludedPathNavigation() {
  const pathname = usePathname();

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = isWingifyLoaded() ? excludedLinkTarget(event) : undefined;
      if (target) {
        // Next.js <Link> skips client navigation for prevented clicks.
        event.preventDefault();
        assignDocument(target);
      }
    };

    // Capture phase runs before React's root listener, where <Link> handles clicks.
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  useLayoutEffect(() => {
    if (WINGIFY_EXCLUDED_PATH.test(pathname) && isWingifyLoaded()) {
      reloadDocument();
    }
  }, [pathname]);

  return null;
}
